/**
 * Spend across campaigns (spec §8, subsystem 3).
 *
 * Subsystem 1 put one figure on one project header: committed spend against
 * the agreed internal cost. This is the same question asked across every
 * campaign at once — the view that answers "where are we losing money" rather
 * than "is this campaign inside its numbers".
 *
 * Committed spend is gross and available today. The net column goes through
 * `tax.js`, so it is exact for invoices finance has entered tax on and an
 * estimate for the rest; the row says which it is rather than presenting a
 * guess as a figure.
 *
 * Pure. The caller fetches the campaigns and the invoices and passes them in.
 */

import { margin } from './commercials';
import { netPayable, roundMoney } from './tax';
import { toCsv } from './bankFile';

const isBlank = (value) =>
  value === null || value === undefined || String(value).trim() === '';

function number(value) {
  if (isBlank(value)) return null;
  const parsed = typeof value === 'number' ? value : parseFloat(String(value).trim());
  return Number.isFinite(parsed) ? parsed : null;
}

const amount = (value) => number(value) ?? 0;
const text = (value) => (isBlank(value) ? '' : String(value).trim());

/** The columns, in the order they are read and exported. */
export const ROLLUP_COLUMNS = [
  { key: 'name', header: 'Campaign', type: 'text' },
  { key: 'brand', header: 'Brand', type: 'text' },
  { key: 'client_cost', header: 'Client cost', type: 'money' },
  { key: 'internal_cost', header: 'Agreed internal cost', type: 'money' },
  { key: 'invoiced', header: 'Invoiced', type: 'money' },
  { key: 'paid', header: 'Paid', type: 'money' },
  { key: 'outstanding', header: 'Outstanding', type: 'money' },
  { key: 'variance', header: 'Variance', type: 'money' },
  { key: 'percent', header: 'Spend %', type: 'number' },
  { key: 'margin', header: 'Margin', type: 'money' },
  { key: 'count', header: 'Invoices', type: 'number' },
];

/** A rejected invoice was never a commitment. */
const committed = (invoice) => invoice && invoice.status !== 'rejected';

/**
 * The campaigns an invoice list refers to, one each, in the order first seen.
 *
 * The invoice payload names its campaign but carries none of the commercial
 * block, so a rollup built this way reports invoiced, paid and outstanding
 * today and gains the agreed cost and margin columns when the campaign
 * endpoint starts returning them. `extra` lets a caller merge in whatever
 * commercials it already holds.
 */
export function campaignsFromInvoices(invoices, extra) {
  const list = Array.isArray(invoices) ? invoices.filter(Boolean) : [];
  const byId = Array.isArray(extra)
    ? extra.filter(Boolean).reduce((carry, campaign) => ({ ...carry, [campaign.id]: campaign }), {})
    : {};

  return list.reduce((campaigns, invoice) => {
    const id = invoice.campaign_id;
    if (isBlank(id) || campaigns.some((campaign) => campaign.id === id)) return campaigns;
    return [
      ...campaigns,
      { id, name: text(invoice.campaign_name), brand_name: text(invoice.brand_name), ...byId[id] },
    ];
  }, []);
}

/** One row per campaign: what was agreed, what has been invoiced and paid. */
export function rollupRows(campaigns, invoices) {
  const list = Array.isArray(campaigns) ? campaigns.filter(Boolean) : [];
  const all = Array.isArray(invoices) ? invoices.filter(committed) : [];

  return list.map((campaign) => {
    const mine = all.filter((invoice) => invoice.campaign_id === campaign.id);

    const invoiced = mine.reduce((total, invoice) => total + amount(invoice.amount), 0);
    const paid = mine
      .filter((invoice) => invoice.status === 'paid')
      .reduce((total, invoice) => total + amount(invoice.amount), 0);

    const taxed = mine.map((invoice) => netPayable(invoice));
    const internalCost = amount(campaign.internal_cost);
    const agreed = internalCost > 0;

    return {
      campaign_id: campaign.id,
      name: text(campaign.name) || text(campaign.campaign_name),
      brand: text(campaign.brand_name) || text(campaign.brand),
      client_cost: amount(campaign.client_cost),
      internal_cost: internalCost,
      margin: margin(campaign),
      invoiced: roundMoney(invoiced),
      paid: roundMoney(paid),
      outstanding: roundMoney(invoiced - paid),
      net: roundMoney(taxed.reduce((total, tax) => total + tax.net, 0)),
      estimated: taxed.some((tax) => tax.estimated),
      count: mine.length,
      // Null, not zero: there is nothing to measure against until a cost has
      // been agreed, and comparing against zero is noise (spec §4.4).
      variance: agreed ? roundMoney(invoiced - internalCost) : null,
      percent: agreed ? Math.min(100, Math.round((invoiced / internalCost) * 100)) : null,
      over: agreed && invoiced > internalCost,
    };
  });
}

/** The columns that can be added up, plus how many campaigns are over. */
export function rollupTotals(rows) {
  const list = Array.isArray(rows) ? rows : [];
  return list.reduce(
    (totals, row) => ({
      campaigns: totals.campaigns + 1,
      client_cost: roundMoney(totals.client_cost + row.client_cost),
      internal_cost: roundMoney(totals.internal_cost + row.internal_cost),
      invoiced: roundMoney(totals.invoiced + row.invoiced),
      paid: roundMoney(totals.paid + row.paid),
      outstanding: roundMoney(totals.outstanding + row.outstanding),
      net: roundMoney(totals.net + row.net),
      estimated: totals.estimated || Boolean(row.estimated),
      overCount: totals.overCount + (row.over ? 1 : 0),
    }),
    {
      campaigns: 0,
      client_cost: 0,
      internal_cost: 0,
      invoiced: 0,
      paid: 0,
      outstanding: 0,
      net: 0,
      estimated: false,
      overCount: 0,
    },
  );
}

const columnByKey = (key) => ROLLUP_COLUMNS.find((column) => column.key === key) || null;

/**
 * Sorted copy. A null figure sorts last whichever direction is asked for —
 * "not agreed yet" is not the smallest number, it is the absence of one.
 */
export function sortRollup(rows, key, direction = 'desc') {
  const list = Array.isArray(rows) ? [...rows] : [];
  const column = columnByKey(key);
  if (!column) return list;

  const sign = direction === 'asc' ? 1 : -1;

  return list.sort((a, b) => {
    const left = a[key];
    const right = b[key];

    if (left === null && right === null) return 0;
    if (left === null) return 1;
    if (right === null) return -1;

    if (column.type === 'text') {
      return String(left).localeCompare(String(right)) * sign;
    }
    return (amount(left) - amount(right)) * sign;
  });
}

/** Narrow the rows. Everything is optional; nothing given means everything. */
export function filterRollup(rows, options = {}) {
  const list = Array.isArray(rows) ? rows : [];
  const { brand, overOnly, search } = options || {};
  const needle = text(search).toLowerCase();

  return list.filter((row) => {
    if (!isBlank(brand) && row.brand !== brand) return false;
    if (overOnly && !row.over) return false;
    if (needle) {
      const haystack = `${row.name} ${row.brand}`.toLowerCase();
      if (!haystack.includes(needle)) return false;
    }
    return true;
  });
}

/** A blank cell for a figure that does not exist, rather than a misleading 0. */
const cellOf = (row, column) => {
  const value = row[column.key];
  return value === null || value === undefined ? '' : value;
};

/** The rollup as a CSV, for the spreadsheet this view replaces. */
export function rollupCsv(rows) {
  const list = Array.isArray(rows) ? rows : [];
  return toCsv([
    ROLLUP_COLUMNS.map((column) => column.header),
    ...list.map((row) => ROLLUP_COLUMNS.map((column) => cellOf(row, column))),
  ]);
}

/**
 * How much of the filtered set a rollup was built from.
 *
 * The console pages at 25, and the spend panel used to be handed that page.
 * A total built from a page is not a smaller total, it is a wrong one, and it
 * sat beside the server's correct figure with nothing to tell them apart.
 *
 * An unknown total means there is nothing to compare against, so what was
 * loaded is treated as everything rather than reported as a shortfall nobody
 * can act on.
 */
export function spendCoverage(loaded, total) {
  const have = Number.isFinite(Number(loaded)) ? Math.max(0, Number(loaded)) : 0;
  const claimed = Number.isFinite(Number(total)) ? Math.max(0, Number(total)) : 0;
  const whole = Math.max(have, claimed);

  return { loaded: have, total: whole, missing: whole - have, complete: have >= whole };
}
